import {
  SET_SELECTED_CALENDAR,
  SET_PLANNER_MODE,
  SET_LOADED_WEEK_RANGE,
  LOAD_MORE_WEEKS_START,
  LOAD_MORE_WEEKS_FULFILLED,
  LOAD_MORE_WEEKS_REJECTED,
  LOAD_MORE_WEEKS_AND_BLOCKS_FULFILLED,
  CREATE_PLAN_FULFILLED,
  CREATE_BLOCK_FULFILLED,
  UPDATE_BLOCK_FULFILLED,
  UPDATE_MULTIPLE_BLOCKS_FULFILLED,
  DELETE_BLOCK_FULFILLED,
  FETCH_PLANNER_START,
  FETCH_PLANNER_FULFILLED,
  FETCH_PLANNER_REJECTED,
  UPSERT_NOTE_FULFILLED,
  DELETE_NOTE_FULFILLED,
  SET_PLANNER_EXAMPLES_MODAL_OPEN,
  SET_SELECTED_CALENDAR_NAME,
} from './actionTypes';

import { axiosAuthed } from '../../../shared/utils/setCommonHeaders';
import {
  addWeeks,
  getCurrentWeek,
  getWeekRangeDates,
  weekToDateRange,
  dateToWeek,
} from '../utils/weekUtils';
import { colorIdToHex, hexToColorId } from '../components/blockColors';

// Configuration constant for how many weeks to load at a time
export const WEEKS_TO_LOAD = 24;

/**
 * Fetch or create a plan for a calendar
 */
const fetchOrCreatePlan = async (accountCode, calendarId = null, userId = null) => {
  const createPayload = {};
  if (calendarId) {
    createPayload.calendarId = calendarId;
  } else if (userId) {
    createPayload.userId = userId;
  }

  try {
    const response = await axiosAuthed.get(`/accounts/${accountCode}/plans/calendars`);

    const data = response.data || {};
    const accountCalendars = data.accountCalendars || [];
    const athleteCalendars = data.athleteCalendars || [];

    let existingCalendar;
    if (calendarId) {
      existingCalendar = accountCalendars.find((cal) => cal.id === calendarId);
    } else if (userId) {
      existingCalendar = athleteCalendars.find((cal) => cal.id === userId);
    }

    if (existingCalendar && existingCalendar.planId) {
      const planResponse = await axiosAuthed.get(
        `/accounts/${accountCode}/plans/${existingCalendar.planId}`,
      );
      return planResponse;
    }

    return axiosAuthed.post(`/accounts/${accountCode}/plans`, createPayload);
  } catch (error) {
    // Only retry on 404 - rethrow network errors and other failures
    if (error.response && error.response.status === 404) {
      return axiosAuthed.post(`/accounts/${accountCode}/plans`, createPayload);
    }
    throw error;
  }
};

/**
 * Fetch plan data with blocks and notes in a date range
 */
const fetchPlanData = async (accountCode, planId, startDate, endDate) => axiosAuthed.get(
  `/accounts/${accountCode}/plans/${planId}`,
  {
    params: { startDate, endDate },
  },
);

/**
 * Bulk create or update blocks
 */
const createOrUpdateBlocks = async (accountCode, planId, blocksArray) => {
  const apiBlocks = blocksArray.map((block) => {
    const apiBlock = {
      startDate: block.startDate,
      endDate: block.endDate,
      row: block.row,
      name: block.name,
      color: colorIdToHex(block.colorId || block.color),
    };

    if (block.id && !String(block.id).startsWith('temp_')) {
      apiBlock.id = parseInt(block.id, 10);
    }

    return apiBlock;
  });

  return axiosAuthed.put(
    `/accounts/${accountCode}/plans/${planId}/blocks`,
    apiBlocks,
  );
};

/**
 * Delete a single block
 */
const deleteBlockAPI = async (accountCode, planId, blockId) => {
  const numericBlockId = parseInt(blockId, 10);
  return axiosAuthed.delete(
    `/accounts/${accountCode}/plans/${planId}/blocks/${numericBlockId}`,
  );
};

/**
 * Create a note
 */
const createNote = async (accountCode, planId, date, noteText) => axiosAuthed.post(
  `/accounts/${accountCode}/plans/${planId}/notes`,
  { date, note: noteText },
);

/**
 * Update a note
 */
const updateNote = async (accountCode, planId, noteId, noteText) => axiosAuthed.put(
  `/accounts/${accountCode}/plans/${planId}/notes/${noteId}`,
  { note: noteText },
);

/**
 * Normalize API blocks to Redux format
 */
const normalizeBlocks = (blocksArray) => {
  if (!Array.isArray(blocksArray)) {
    return {};
  }

  const result = blocksArray
    .filter((block) => {
      if (!block) {
        return false;
      }
      // Filter soft-deleted
      if (block.del) {
        return false;
      }
      return true;
    })
    .reduce((acc, block) => {
      if (!block.id) {
        return acc;
      }

      const fromWeek = dateToWeek(block.startDate);
      const toWeek = dateToWeek(block.endDate);

      const normalizedBlock = {
        id: String(block.id),
        planId: block.planId,
        name: block.name || '',
        colorId: hexToColorId(block.color),
        row: block.row || 0,
        startDate: block.startDate,
        endDate: block.endDate,
        fromWeek,
        toWeek,
        startWeek: fromWeek,
        endWeek: toWeek,
        createdAt: block.createdAt,
        updatedAt: block.updatedAt,
      };

      acc[String(block.id)] = normalizedBlock;
      return acc;
    }, {});

  return result;
};

/**
 * Normalize API notes to Redux format
 */
const normalizeNotes = (notesArray) => {
  if (!Array.isArray(notesArray)) return {};

  return notesArray
    .filter((note) => !note.del)
    .reduce((acc, note) => {
      const week = dateToWeek(note.date);
      acc[week] = {
        id: note.id,
        week,
        text: note.note,
        date: note.date,
        updatedAt: note.updatedAt,
      };
      return acc;
    }, {});
};

/**
 * Set the selected calendar
 */
export const setSelectedCalendar = (calendarId) => ({
  type: SET_SELECTED_CALENDAR,
  payload: calendarId,
});

/**
 * Set the planner mode ('plan' or 'review')
 */
export const setPlannerMode = (mode) => ({
  type: SET_PLANNER_MODE,
  payload: mode,
});

/**
 * Set the loaded week range
 */
export const setLoadedWeekRange = (fromWeek, toWeek) => ({
  type: SET_LOADED_WEEK_RANGE,
  payload: { fromWeek, toWeek },
});

/**
 * Initialize loaded week range (current week +/- WEEKS_TO_LOAD)
 */
export const initializeWeekRange = () => (dispatch) => {
  const currentWeek = getCurrentWeek();
  const fromWeek = addWeeks(currentWeek, -WEEKS_TO_LOAD);
  const toWeek = addWeeks(currentWeek, WEEKS_TO_LOAD);

  dispatch(setLoadedWeekRange(fromWeek, toWeek));

  return {
    fromWeek,
    toWeek,
  };
};

/**
 * Load more weeks in the past (scroll left)
 */
export const loadMoreWeeksPast = (currentFromWeek) => async (
  dispatch,
  getState,
) => {
  dispatch({ type: LOAD_MORE_WEEKS_START });

  try {
    const state = getState().planner;
    const { planId } = state.data;
    const { accountCode } = getState().auth.data.currentUser;
    const { toWeek } = state.ui.loadedWeekRange;

    const newFromWeek = addWeeks(currentFromWeek, -WEEKS_TO_LOAD);

    // Load blocks and notes for this range
    if (planId && accountCode) {
      // Request weeks from newFromWeek up to (but NOT including) currentFromWeek
      // to avoid fetching already-loaded blocks
      const endWeekForFetch = addWeeks(currentFromWeek, -1);
      const { startDate, endDate } = getWeekRangeDates(
        newFromWeek,
        endWeekForFetch,
      );

      // Fetch plan data with blocks and notes
      const response = await fetchPlanData(
        accountCode,
        planId,
        startDate,
        endDate,
      );

      const planData = response.data;

      // Normalize and merge blocks without duplicates
      const newBlocks = normalizeBlocks(planData.planBlocks || []);
      const existingBlockIds = new Set(Object.keys(state.data.blocks));
      const blocksToAdd = {};
      Object.entries(newBlocks).forEach(([id, block]) => {
        if (!existingBlockIds.has(String(id))) {
          blocksToAdd[id] = block;
        }
      });

      // Normalize and merge notes without duplicates
      const newNotes = normalizeNotes(planData.planNotes || []);
      const existingNoteWeeks = new Set(Object.keys(state.data.notes));
      const notesToAdd = {};
      Object.entries(newNotes).forEach(([week, note]) => {
        if (!existingNoteWeeks.has(week)) {
          notesToAdd[week] = note;
        }
      });

      // Batched dispatch: update blocks, notes, and week range in single action
      dispatch({
        type: LOAD_MORE_WEEKS_AND_BLOCKS_FULFILLED,
        payload: {
          blocks: blocksToAdd,
          notes: notesToAdd,
          fromWeek: newFromWeek,
          toWeek,
        },
      });
    } else {
      // No plan yet, just update week range
      dispatch({
        type: LOAD_MORE_WEEKS_FULFILLED,
        payload: { fromWeek: newFromWeek, toWeek },
      });
    }

    return { newFromWeek };
  } catch (err) {
    dispatch({
      type: LOAD_MORE_WEEKS_REJECTED,
      payload: err.message || 'Failed to load more weeks',
    });
    throw err;
  }
};

/**
 * Load more weeks in the future (scroll right)
 */
export const loadMoreWeeksFuture = (currentToWeek) => async (
  dispatch,
  getState,
) => {
  dispatch({ type: LOAD_MORE_WEEKS_START });

  try {
    const state = getState().planner;
    const { planId } = state.data;
    const { accountCode } = getState().auth.data.currentUser;
    const { fromWeek } = state.ui.loadedWeekRange;

    const newToWeek = addWeeks(currentToWeek, WEEKS_TO_LOAD);

    // Load blocks and notes for this range
    if (planId && accountCode) {
      // Request weeks from (but NOT including) currentToWeek to newToWeek
      // to avoid fetching already-loaded blocks
      const startWeekForFetch = addWeeks(currentToWeek, 1);
      const { startDate, endDate } = getWeekRangeDates(
        startWeekForFetch,
        newToWeek,
      );

      // Fetch plan data with blocks and notes
      const response = await fetchPlanData(
        accountCode,
        planId,
        startDate,
        endDate,
      );

      const planData = response.data;

      // Normalize and merge blocks without duplicates
      const newBlocks = normalizeBlocks(planData.planBlocks || []);
      const existingBlockIds = new Set(Object.keys(state.data.blocks));
      const blocksToAdd = {};
      Object.entries(newBlocks).forEach(([id, block]) => {
        if (!existingBlockIds.has(String(id))) {
          blocksToAdd[id] = block;
        }
      });

      // Normalize and merge notes without duplicates
      const newNotes = normalizeNotes(planData.planNotes || []);
      const existingNoteWeeks = new Set(Object.keys(state.data.notes));
      const notesToAdd = {};
      Object.entries(newNotes).forEach(([week, note]) => {
        if (!existingNoteWeeks.has(week)) {
          notesToAdd[week] = note;
        }
      });

      // Batched dispatch: update blocks, notes, and week range in single action
      dispatch({
        type: LOAD_MORE_WEEKS_AND_BLOCKS_FULFILLED,
        payload: {
          blocks: blocksToAdd,
          notes: notesToAdd,
          fromWeek,
          toWeek: newToWeek,
        },
      });
    } else {
      // No plan yet, just update week range
      dispatch({
        type: LOAD_MORE_WEEKS_FULFILLED,
        payload: { fromWeek, toWeek: newToWeek },
      });
    }

    return { newToWeek };
  } catch (err) {
    dispatch({
      type: LOAD_MORE_WEEKS_REJECTED,
      payload: err.message || 'Failed to load more weeks',
    });
    throw err;
  }
};

/**
 * Fetch planner data for a calendar or athlete (creates plan if doesn't exist)
 * @param {string|number} accountCode - The account code
 * @param {number|null} calendarId - The calendar ID (for calendar groups)
 * @param {number|null} userId - The user ID (for athlete calendars)
 */
export const fetchPlanner = (
  accountCode,
  calendarId = null,
  userId = null,
) => async (dispatch, getState) => {
  dispatch({ type: FETCH_PLANNER_START });

  try {
    // Get or create plan
    const planResponse = await fetchOrCreatePlan(
      accountCode,
      calendarId,
      userId,
    );
    const plan = planResponse.data;
    const planId = plan.id;

    // Dispatch plan creation success
    dispatch({
      type: CREATE_PLAN_FULFILLED,
      payload: {
        planId,
        calendarId: plan.calendarId,
      },
    });

    // Calculate initial week range
    const state = getState().planner;
    const { fromWeek, toWeek } = state.ui.loadedWeekRange;

    if (!fromWeek || !toWeek) {
      dispatch({
        type: FETCH_PLANNER_REJECTED,
        payload: 'Week range not initialized',
      });
      throw new Error('Week range not initialized');
    }

    const { startDate, endDate } = getWeekRangeDates(fromWeek, toWeek);

    // Fetch plan data with blocks and notes using the plan endpoint
    const planDataResponse = await fetchPlanData(
      accountCode,
      planId,
      startDate,
      endDate,
    );

    // Extract blocks and notes from plan data
    const planData = planDataResponse.data;
    const blocks = normalizeBlocks(planData.planBlocks || []);
    const notes = normalizeNotes(planData.planNotes || []);

    dispatch({
      type: FETCH_PLANNER_FULFILLED,
      payload: {
        planId,
        calendarId,
        blocks,
        notes,
      },
    });
  } catch (err) {
    dispatch({
      type: FETCH_PLANNER_REJECTED,
      payload: err.message || 'Failed to fetch planner data',
    });
    throw err;
  }
};

/**
 * Create a new block
 */
export const createBlock = (blockData) => async (dispatch, getState) => {
  const state = getState().planner;
  const { planId } = state.data;
  const { accountCode } = getState().auth.data.currentUser;

  if (!planId || !accountCode) {
    throw new Error('Planner not initialized');
  }

  const response = await createOrUpdateBlocks(accountCode, planId, [blockData]);

  const createdBlocks = response.data;
  const normalizedBlocks = normalizeBlocks(createdBlocks);
  const normalizedBlock = Object.values(normalizedBlocks)[0];

  if (!normalizedBlock) {
    throw new Error('Failed to create block: invalid response from server');
  }

  dispatch({
    type: CREATE_BLOCK_FULFILLED,
    payload: { block: normalizedBlock },
  });

  return normalizedBlock;
};

/**
 * Update an existing block
 */
export const updateBlock = (blockId, updates) => async (dispatch, getState) => {
  const state = getState().planner;
  const { planId } = state.data;
  const { accountCode } = getState().auth.data.currentUser;
  const originalBlock = state.data.blocks[blockId];

  if (!originalBlock) {
    return null;
  }

  // Optimistic update
  const optimisticBlock = { ...originalBlock, ...updates };
  dispatch({
    type: UPDATE_BLOCK_FULFILLED,
    payload: { block: optimisticBlock },
  });

  try {
    const response = await createOrUpdateBlocks(accountCode, planId, [
      optimisticBlock,
    ]);

    // Confirm with server data
    const updatedBlocks = response.data;
    const normalizedBlocks = normalizeBlocks(updatedBlocks);

    // Get the first (and only) normalized block
    const normalizedBlock = Object.values(normalizedBlocks)[0];

    if (!normalizedBlock) {
      throw new Error('Failed to update block: invalid response from server');
    }

    dispatch({
      type: UPDATE_BLOCK_FULFILLED,
      payload: { block: normalizedBlock },
    });

    return normalizedBlock;
  } catch (err) {
    // Silent revert
    dispatch({
      type: UPDATE_BLOCK_FULFILLED,
      payload: { block: originalBlock },
    });
    throw err;
  }
};

/**
 * Update multiple blocks at once (for vertical drag operations)
 * Uses batched dispatch to reduce re-renders
 */
export const updateMultipleBlocks = (blockUpdates) => async (
  dispatch,
  getState,
) => {
  const state = getState().planner;
  const { planId } = state.data;
  const { accountCode } = getState().auth.data.currentUser;
  const originalBlocks = {};

  // Store originals for rollback and build optimistic updates
  const optimisticBlocks = {};
  blockUpdates.forEach(({ blockId, updates }) => {
    const block = state.data.blocks[blockId];
    if (block) {
      originalBlocks[blockId] = block;
      optimisticBlocks[blockId] = { ...block, ...updates };
    }
  });

  // Single batched optimistic update
  dispatch({
    type: UPDATE_MULTIPLE_BLOCKS_FULFILLED,
    payload: { blocks: optimisticBlocks },
  });

  try {
    const blocksArray = Object.values(optimisticBlocks);
    await createOrUpdateBlocks(accountCode, planId, blocksArray);
  } catch (err) {
    // Single batched revert
    dispatch({
      type: UPDATE_MULTIPLE_BLOCKS_FULFILLED,
      payload: { blocks: originalBlocks },
    });
    throw err;
  }
};

/**
 * Delete a block
 */
export const deleteBlock = (blockId) => async (dispatch, getState) => {
  const state = getState().planner;
  const { planId } = state.data;
  const { accountCode } = getState().auth.data.currentUser;
  const originalBlock = state.data.blocks[blockId];

  if (!originalBlock) {
    return null;
  }

  // Optimistic delete
  dispatch({
    type: DELETE_BLOCK_FULFILLED,
    payload: { blockId },
  });

  try {
    await deleteBlockAPI(accountCode, planId, blockId);
    return { blockId };
  } catch (err) {
    // Silent revert
    dispatch({
      type: CREATE_BLOCK_FULFILLED,
      payload: { block: originalBlock },
    });
    throw err;
  }
};

/**
 * Upsert a note for a specific week
 */
export const upsertNote = (week, noteText) => async (dispatch, getState) => {
  const state = getState().planner;
  const { planId } = state.data;
  const { accountCode } = getState().auth.data.currentUser;
  const existingNote = state.data.notes[week];

  // Optimistic update
  const optimisticNote = {
    id: existingNote?.id,
    week,
    text: noteText,
    date: existingNote?.date || weekToDateRange(week).startDate,
    updatedAt: Math.floor(Date.now() / 1000),
  };

  dispatch({
    type: UPSERT_NOTE_FULFILLED,
    payload: { week, note: optimisticNote },
  });

  try {
    const { startDate } = weekToDateRange(week); // Monday of the week

    if (existingNote?.id) {
      await updateNote(accountCode, planId, existingNote.id, noteText);
    } else {
      const response = await createNote(accountCode, planId, startDate, noteText);
      // Update with real ID
      const normalizedNote = normalizeNotes([response.data])[week];
      dispatch({
        type: UPSERT_NOTE_FULFILLED,
        payload: { week, note: normalizedNote },
      });
    }
  } catch (err) {
    // Silent revert
    if (existingNote) {
      dispatch({
        type: UPSERT_NOTE_FULFILLED,
        payload: { week, note: existingNote },
      });
    } else {
      dispatch({
        type: DELETE_NOTE_FULFILLED,
        payload: { week },
      });
    }
    throw err;
  }
};

/**
 * Set planner examples modal open state
 */
export const setPlannerExamplesModalOpen = (isOpen) => ({
  type: SET_PLANNER_EXAMPLES_MODAL_OPEN,
  payload: isOpen,
});

/**
 * Set the selected calendar name
 */
export const setSelectedCalendarName = (calendarName) => ({
  type: SET_SELECTED_CALENDAR_NAME,
  payload: calendarName,
});

/**
 * Fetch the last planned block for a plan
 * Used to determine the default "To" date for PDF export
 * @param {string|number} accountCode - The account code
 * @param {number} planId - The plan ID
 * @returns {Promise<Object|null>} The last planned block or null if none exist
 */
export const fetchLastPlannedBlock = (accountCode, planId) => async () => {
  try {
    const response = await axiosAuthed.get(
      `/accounts/${accountCode}/plans/${planId}/blocks/last-planned`,
    );
    // API returns empty object {} if no blocks exist
    return response.data && response.data.endDate ? response.data : null;
  } catch (error) {
    return null;
  }
};
